"use client";

import { useEffect, useState } from "react";
import PortfolioSidebar from "../../components/portfoliosidebar/PortfolioSidebar";
import GlareHover from "../../components/GlareHover/GlareHover";
import SplitText from "../../components/SplitText/SplitText";
import "./Home.css";

export default function Home() {
  const [activeSection, setActiveSection] = useState("about");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["about", "projects", "contact"];
      const isMobile = window.innerWidth <= 768;
      const scrollOffset = isMobile ? 200 : 550;
      const scrollPosition = window.scrollY + scrollOffset;

      for (const sectionId of sections) {
        const section = document.getElementById(sectionId);
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionHeight = section.offsetHeight;

          if (
            scrollPosition >= sectionTop &&
            scrollPosition < sectionTop + sectionHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
    }
  };

  const handleAnimationComplete = () => {
    console.log("All letters have animated!");
  };

  return (
    <div className="portfolio-container">
      <PortfolioSidebar
        activeSection={activeSection}
        onNavigate={scrollToSection}
      />

      <main className="main-content">
        <section id="about" className="section section-white">
          <div className="section-content">
            <SplitText
              text="About me!"
              className="section-title"
              delay={100}
              duration={0.6}
              ease="power3.out"
              splitType="chars"
              from={{ opacity: 0, y: 40 }}
              to={{ opacity: 1, y: 0 }}
              threshold={0.1}
              rootMargin="-100px"
              textAlign="center"
              onLetterAnimationComplete={handleAnimationComplete}
            />
            <p className="section-text">
              Hello! I'm Ersoy, originally from Cyprus and currently pursuing my
              Master's degree in Data Science and Artificial Intelligence at TU
              Eindhoven, building on my Bachelor's in Computer Science and
              Engineering from the same university. Outside my studies, I love
              exploring new technologies and continuously improving my skills
              through personal projects.
            </p>
            <p className="section-text">
              I am passionate about AI and machine learning, always following
              the latest advancements in the field. I enjoy learning and
              building applications that leverage these technologies to solve
              real-world problems.
            </p>
            <p className="section-text">
              I am also the Co-Founder of{" "}
              <a
                href="https://elevates.it/en"
                target="_blank"
                rel="noopener noreferrer"
              >
                Elevates
              </a>
              , a startup focused on digital products that aim to deliver top
              quality products. My role involves leading the technical
              development and ensuring our solutions meet user needs
              effectively.
            </p>
          </div>
        </section>

        <section id="projects" className="section section-gray">
          <div className="section-content">
            <SplitText
              text="Projects"
              className="section-title"
              delay={100}
              duration={0.6}
              ease="power3.out"
              splitType="chars"
              from={{ opacity: 0, y: 40 }}
              to={{ opacity: 1, y: 0 }}
              threshold={0.1}
              rootMargin="-100px"
              textAlign="center"
              onLetterAnimationComplete={handleAnimationComplete}
            />

            <div className="projects-list">
              <a
                href="https://homestylecy.com"
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                <GlareHover
                  glareColor="#ffffff"
                  glareOpacity={0.2}
                  glareAngle={-30}
                  glareSize={300}
                  transitionDuration={1200}
                  playOnce={false}
                >
                  <div className="project-card">
                    <div className="project-header">
                      <h3 className="project-title">
                        H.S Home Style - Home Appliances Catalog
                      </h3>
                      <span className="project-date">October 2026</span>
                    </div>
                    <p className="project-description">
                      Built a bilingual (Turkish/English) product catalog for a
                      family-run home appliances store in Nicosia, delivered in
                      partnership with Elevates. The site showcases 1,000+
                      products across categories like white goods, kitchen
                      appliances and TVs, with search, sale listings and
                      WhatsApp-based ordering. Store staff manage products,
                      categories and content through a Payload CMS admin panel
                      backed by PostgreSQL.
                    </p>

                    <div className="project-tags">
                      <span className="tag tag-blue">Next.js</span>
                      <span className="tag tag-purple">Payload CMS</span>
                      <span className="tag tag-cyan">PostgreSQL</span>
                      <span className="tag tag-green">E-Commerce Catalog</span>
                      <span className="tag tag-orange">SEO</span>
                    </div>
                  </div>
                </GlareHover>
              </a>
              <a
                href="https://hacialimotors.com"
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                <GlareHover
                  glareColor="#ffffff"
                  glareOpacity={0.2}
                  glareAngle={-30}
                  glareSize={300}
                  transitionDuration={1200}
                  playOnce={false}
                >
                  <div className="project-card">
                    <div className="project-header">
                      <h3 className="project-title">
                        Hacıali Motors - Car Dealership Platform
                      </h3>
                      <span className="project-date">October 2026</span>
                    </div>
                    <p className="project-description">
                      Built a bilingual (Turkish/English) used-car dealership
                      website for a Nicosia-based dealer, delivered in
                      partnership with Elevates. Features vehicle listings with
                      filtering and sorting, side-by-side comparison of up to
                      three cars, per-vehicle history files, favourites,
                      trade-in valuation and test drive requests. The dealer
                      manages vehicle inventory through a Payload CMS admin
                      panel backed by PostgreSQL.
                    </p>

                    <div className="project-tags">
                      <span className="tag tag-blue">Next.js</span>
                      <span className="tag tag-purple">Payload CMS</span>
                      <span className="tag tag-cyan">PostgreSQL</span>
                      <span className="tag tag-green">
                        Full-Stack Development
                      </span>
                      <span className="tag tag-orange">SEO</span>
                    </div>
                  </div>
                </GlareHover>
              </a>
              <a
                href="/BEP_Report.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                <GlareHover
                  glareColor="#ffffff"
                  glareOpacity={0.2}
                  glareAngle={-30}
                  glareSize={300}
                  transitionDuration={1200}
                  playOnce={false}
                >
                  <div className="project-card">
                    <div className="project-header">
                      <h3 className="project-title">
                        Bachelor End Project - Integrating Feedback Mechanisms
                        with AI Agents for Robust Autonomous Driving
                      </h3>
                      <span className="project-date">
                        January 2026 - June 2026
                      </span>
                    </div>
                    <p className="project-description">
                      Completed my Bachelor End Project at Eindhoven University
                      of Technology, graded 8.5/10, under the supervision of
                      Valentina Castiglioni. The project explored how feedback
                      mechanisms can be integrated with AI agents to make
                      autonomous driving behaviour more robust. Over six months
                      I learned to break down a hard research problem
                      independently, stay patient through failed iterations, and
                      communicate complex ideas simply in a ten-minute defence.
                    </p>

                    <div className="project-tags">
                      <span className="tag tag-purple">AI Agents</span>
                      <span className="tag tag-blue">Autonomous Driving</span>
                      <span className="tag tag-cyan">Feedback Mechanisms</span>
                      <span className="tag tag-green">Research</span>
                      <span className="tag tag-purple">STARK</span>
                      <span className="tag tag-orange">Python</span>
                      <span className="tag tag-green-light">Java</span>
                    </div>
                  </div>
                </GlareHover>
              </a>
              <a
                href="https://mun.ersoybaki.com"
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                <GlareHover
                  glareColor="#ffffff"
                  glareOpacity={0.2}
                  glareAngle={-30}
                  glareSize={300}
                  transitionDuration={1200}
                  playOnce={false}
                >
                  <div className="project-card">
                    <div className="project-header">
                      <h3 className="project-title">
                        Elevates MUN (Demo Conference Website)
                      </h3>
                      <span className="project-date">
                        December 2025 – February 2026
                      </span>
                    </div>

                    <p className="project-description">
                      Designed and developed a responsive Model United Nations
                      (MUN) conference website as a demo placeholder. The
                      platform showcases committee information, registration
                      sections, conference agenda, and contact forms. Built with
                      a modern, clean interface to reflect professionalism and
                      diplomatic branding standards.
                    </p>

                    <div className="project-tags">
                      <span className="tag tag-blue">React.js</span>
                      <span className="tag tag-green">Responsive Design</span>
                      <span className="tag tag-purple">UI/UX</span>
                      <span className="tag tag-green-light">
                        Frontend Development
                      </span>
                      <span className="tag tag-orange">SEO</span>
                    </div>
                  </div>
                </GlareHover>
              </a>
              <GlareHover
                glareColor="#ffffff"
                glareOpacity={0.2}
                glareAngle={-30}
                glareSize={300}
                transitionDuration={1200}
                playOnce={false}
              >
                <div className="project-card">
                  <div className="project-header">
                    <h3 className="project-title">Gaslab Chronos - VR Game</h3>
                    <span className="project-date">
                      September 2025 - January 2026
                    </span>
                  </div>
                  <p className="project-description">
                    Part of a 10-member Software Engineering team developing a
                    VR game built in Unreal Engine 5 for the Eindhoven AI
                    Systems Institute (EAISI). The project recreates the TU/e
                    Gaslab building across its historical eras as an interactive
                    educational experience, integrating IoT sensor data and an
                    LLM-powered Q&A assistant. Contributing to visual scripting
                    for player interactions, environment logic, and minigames.
                  </p>
                  <div className="project-tags">
                    <span className="tag tag-purple">Unreal Engine 5</span>
                    <span className="tag tag-blue">VR Development</span>
                    <span className="tag tag-green">Visual Scripting</span>
                    <span className="tag tag-orange">Environment Design</span>
                  </div>
                </div>{" "}
              </GlareHover>
              <a
                href="https://github.com/ersoybaki/EventPlannerAI"
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                <GlareHover
                  glareColor="#ffffff"
                  glareOpacity={0.2}
                  glareAngle={-30}
                  glareSize={300}
                  transitionDuration={1200}
                  playOnce={false}
                >
                  <div className="project-card">
                    <div className="project-header">
                      <h3 className="project-title">Event Planner AI</h3>
                      <span className="project-date">
                        June 2025 - August 2025
                      </span>
                    </div>
                    <p className="project-description">
                      Developed an intelligent event-planning web application
                      using Streamlit, OpenAI API, and Google Maps API. The
                      system guides users step-by-step through event planning by
                      collecting preferences and dynamically recommending venues
                      via an interactive map. Implemented fallback logic for
                      expanded search results and a modular multi-agent
                      architecture for personalized recommendations.
                    </p>

                    <div className="project-tags">
                      <span className="tag tag-green">Streamlit</span>
                      <span className="tag tag-blue">OpenAI API</span>
                      <span className="tag tag-green-light">Google Maps</span>
                      <span className="tag tag-purple">Python</span>
                    </div>
                  </div>
                </GlareHover>
              </a>
              <a
                href="https://standoff.ersoybaki.com"
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                <GlareHover
                  glareColor="#ffffff"
                  glareOpacity={0.2}
                  glareAngle={-30}
                  glareSize={300}
                  transitionDuration={1200}
                  playOnce={false}
                >
                  <div className="project-card">
                    <div className="project-header">
                      <h3 className="project-title">
                        Standoff - 2D Dueling Game
                      </h3>
                      <span className="project-date">
                        September 2025 - November 2025
                      </span>
                    </div>
                    <p className="project-description">
                      Built core combat systems for a 2D local PvP dueling game
                      in Godot 4, including hit/hurt boxes, health & stamina
                      systems, timing-based parry mechanics, and death/respawn
                      logic. Synchronized Area2D colliders with animation frames
                      and fixed collision layers/masks to remove self-hits,
                      integrating signals/nodes for reliable gameplay.
                    </p>

                    <div className="project-tags">
                      <span className="tag tag-purple">Godot 4</span>
                      <span className="tag tag-green">Game Development</span>
                      <span className="tag tag-blue">Combat Systems</span>
                    </div>
                  </div>
                </GlareHover>
              </a>
              <a
                href="https://github.com/thea2806/4CBLW00-20"
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                <GlareHover
                  glareColor="#ffffff"
                  glareOpacity={0.2}
                  glareAngle={-30}
                  glareSize={300}
                  transitionDuration={1200}
                  playOnce={false}
                >
                  <div className="project-card">
                    <div className="project-header">
                      <h3 className="project-title">
                        Police Demand Forecasting & Allocation in London
                      </h3>
                      <span className="project-date">
                        April 2025 – June 2025
                      </span>
                    </div>
                    <p className="project-description">
                      Developed a data-driven tool to help the Metropolitan
                      Police of London forecast burglary risk and allocate
                      officer hours more efficiently. The system analyzes crime
                      patterns, neighborhood characteristics, and seasonal
                      trends to highlight high-risk areas and support smarter
                      deployment decisions. Built an interactive dashboard that
                      visualizes hotspots, shows future predictions, and makes
                      resource planning easier for police teams across London.
                    </p>

                    <div className="project-tags">
                      <span className="tag tag-green">LightGBM</span>
                      <span className="tag tag-blue">Python</span>
                      <span className="tag tag-green-light">Streamlit</span>
                      <span className="tag tag-purple">
                        Optimization (CP-SAT)
                      </span>
                      <span className="tag tag-cyan">Machine Learning</span>
                      <span className="tag tag-orange">Folium</span>
                    </div>
                  </div>
                </GlareHover>
              </a>
              <a
                href="https://elevates.it/en"
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                <GlareHover
                  glareColor="#ffffff"
                  glareOpacity={0.2}
                  glareAngle={-30}
                  glareSize={300}
                  transitionDuration={1200}
                  playOnce={false}
                >
                  <div className="project-card">
                    <div className="project-header">
                      <h3 className="project-title">
                        Elevates Company Website
                      </h3>
                      <span className="project-date">
                        October 2025 - November 2025
                      </span>
                    </div>
                    <p className="project-description">
                      Developed the official Elevates company website as
                      Co-Founder, focusing on a clean, responsive, and
                      user-friendly design. Implemented custom components, SEO
                      optimization, and efficient routing to ensure smooth
                      performance. The platform promotes wellness and lifestyle
                      balance with personalized user experiences.
                    </p>

                    <div className="project-tags">
                      <span className="tag tag-blue">React.js</span>
                      <span className="tag tag-green">Responsive Design</span>
                      <span className="tag tag-purple">UI/UX</span>
                      <span className="tag tag-green-light">
                        Frontend Development
                      </span>
                      <span className="tag tag-orange">SEO</span>
                    </div>
                  </div>
                </GlareHover>
              </a>
              <a
                href="https://thevirtue.co"
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                <GlareHover
                  glareColor="#ffffff"
                  glareOpacity={0.2}
                  glareAngle={-30}
                  glareSize={300}
                  transitionDuration={1200}
                  playOnce={false}
                >
                  <div className="project-card">
                    <div className="project-header">
                      <h3 className="project-title">Virtue Startup Webpage</h3>
                      <span className="project-date">
                        September 2024 - April 2025
                      </span>
                    </div>
                    <p className="project-description">
                      Led the design and development of a responsive webpage for
                      a Cyprus-based group of young professionals launching a
                      startup. Delivered a modern and user-friendly digital
                      presence that showcases their vision and services.
                    </p>

                    <div className="project-tags">
                      <span className="tag tag-blue">React.js</span>
                      <span className="tag tag-green">Responsive Design</span>
                      <span className="tag tag-purple">UI/UX</span>
                      <span className="tag tag-green-light">
                        Frontend Development
                      </span>
                      <span className="tag tag-orange">SEO</span>
                    </div>
                  </div>
                </GlareHover>
              </a>
              <GlareHover
                glareColor="#ffffff"
                glareOpacity={0.2}
                glareAngle={-30}
                glareSize={300}
                transitionDuration={1200}
                playOnce={false}
              >
                <div className="project-card">
                  <div className="project-header">
                    <h3 className="project-title">
                      Liberal Democracy Movement Website
                    </h3>
                    <span className="project-date">
                      January 2025 - February 2025
                    </span>
                  </div>
                  <p className="project-description">
                    Lead a team developing a responsive webpage using ReactJS
                    for the Cyprus-based Liberal Democracy Movement, a newly
                    formed organization working towards becoming a political
                    party, ensuring a dynamic and user-friendly interface.
                  </p>

                  <div className="project-tags">
                    <span className="tag tag-blue">React.js</span>
                    <span className="tag tag-green">Responsive Design</span>
                    <span className="tag tag-purple">UI/UX</span>
                    <span className="tag tag-green-light">
                      Frontend Development
                    </span>
                    <span className="tag tag-orange">SEO</span>
                  </div>
                </div>
              </GlareHover>
              <a
                href="https://dairy.ersoybaki.com"
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                <GlareHover
                  glareColor="#ffffff"
                  glareOpacity={0.2}
                  glareAngle={-30}
                  glareSize={300}
                  transitionDuration={1200}
                  playOnce={false}
                >
                  <div className="project-card">
                    <div className="project-header">
                      <h3 className="project-title">Beykoy Dairies Website</h3>
                      <span className="project-date">
                        March 2024 - September 2024
                      </span>
                    </div>
                    <p className="project-description">
                      Lead a team to design and develop a responsive webpage for
                      a Cyprus-based dairy producer using HTML, CSS, and
                      JavaScript, ensuring a visually appealing and
                      user-friendly interface.
                    </p>

                    <div className="project-tags">
                      <span className="tag tag-green">HTML</span>
                      <span className="tag tag-blue">CSS</span>
                      <span className="tag tag-orange">JavaScript</span>
                      <span className="tag tag-purple">SEO</span>
                    </div>
                  </div>
                </GlareHover>
              </a>

              <GlareHover
                glareColor="#ffffff"
                glareOpacity={0.2}
                glareAngle={-30}
                glareSize={300}
                transitionDuration={1200}
                playOnce={false}
              >
                <div className="project-card">
                  <div className="project-header">
                    <h3 className="project-title">
                      3D-Printed Robot Arm Sorting System
                    </h3>
                    <span className="project-date">April 2024 - June 2024</span>
                  </div>
                  <p className="project-description">
                    Successfully led the 3D printing, assembly, and wiring of a
                    robotic arm modeled after the ABB IRB 260 as part of a
                    Bachelor's project. Managed fabrication of 3D-printed
                    components, integrated electrical systems, and programmed
                    movement using Arduino. Developed a React-based control
                    system with real-time monitoring, manual controls, and QR
                    code-based disk sorting.
                  </p>
                  <div className="project-tags">
                    <span className="tag tag-green">React.js</span>
                    <span className="tag tag-blue">Arduino</span>
                    <span className="tag tag-cyan">3D Printing</span>
                    <span className="tag tag-purple">CAD Design</span>
                  </div>
                </div>
              </GlareHover>
            </div>
          </div>
        </section>

        <section id="contact" className="section section-white">
          <div className="section-content">
            <SplitText
              text="Contact me!"
              className="section-title"
              delay={100}
              duration={0.6}
              ease="power3.out"
              splitType="chars"
              from={{ opacity: 0, y: 40 }}
              to={{ opacity: 1, y: 0 }}
              threshold={0.1}
              rootMargin="-100px"
              textAlign="center"
              onLetterAnimationComplete={handleAnimationComplete}
            />
            <p className="section-text contact-intro">
              I'm always interested in hearing about new projects and
              opportunities. Feel free to reach out if you'd like to connect!
            </p>

            <div className="contact-list">
              <div className="contact-item">
                <div className="contact-icon">
                  <a
                    href="&#109;&#97;&#105;&#108;&#116;&#111;&#58;&#105;&#110;&#102;&#111;&#64;&#101;&#114;&#115;&#111;&#121;&#98;&#97;&#107;&#105;&#46;&#99;&#111;&#109;
"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg
                      className="icon"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                    </svg>
                  </a>
                </div>
                <div>
                  <p className="contact-label">Email</p>
                  <p className="contact-value">
                    <a
                      href="&#109;&#97;&#105;&#108;&#116;&#111;&#58;&#105;&#110;&#102;&#111;&#64;&#101;&#114;&#115;&#111;&#121;&#98;&#97;&#107;&#105;&#46;&#99;&#111;&#109;
"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      info@ersoybaki.com
                    </a>
                  </p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <a
                    href="https://linkedin.com/in/ersoy-baki-4607a5314/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg
                      className="icon"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    </svg>
                  </a>
                </div>
                <div>
                  <p className="contact-label">LinkedIn</p>
                  <p className="contact-value">
                    <a
                      href="https://linkedin.com/in/ersoy-baki-4607a5314/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      linkedin.com/in/ersoy-baki-4607a5314/
                    </a>
                  </p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <a
                    href="https://github.com/ersoybaki"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg
                      className="icon"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                    </svg>
                  </a>
                </div>
                <div>
                  <p className="contact-label">GitHub</p>
                  <p className="contact-value">
                    <a
                      href="https://github.com/ersoybaki"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      github.com/ersoybaki
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
